/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from 'Context';
import cn from 'classnames';
import { dataFetcher, endpoints } from 'Api';
import {
  Tooltip,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Button,
  TextField
} from '@material-ui/core';
import {
  Close,
  Flight,
  School,
  People,
  Group,
  PhotoLibrary,
  Work,
  Palette,
  Explore,
  Event,
  Folder,
  Archive,
  Star,
  Category,
  Edit,
  Check,
  CheckCircle,
  Add
} from '@material-ui/icons';
import { ReactComponent as CreateCategory } from 'Assets/create-category.svg';
import AlterImage from 'Assets/alter-image.png';
import styles from './index.module.css';

// Helper function to get optimized ImageKit URL with transformations
// Uses path-based transformation format: /tr:w-{width},f-auto,q-auto/
const getOptimizedImageUrl = (url, width = 200) => {
  if (!url) return null;
  if (url.includes('ik.imagekit.io')) {
    const match = url.match(/^(https:\/\/ik\.imagekit\.io\/[^/]+)\/(.+)$/);
    if (match) {
      const [, base, imagePath] = match;
      return `${base}/tr:w-${width},f-auto,q-auto/${imagePath}`;
    }
  }
  return url;
};

const CreateCategoryPage = ({
  initialOpen = false,
  onModalClose,
  onCategoryCreated,
  editMode = false,
  editData = null, // { id, title, icon }
}) => {
  const [isMenuOpen, setMenuOpen] = useState(initialOpen);
  const [isPresetMode, setIsPresetMode] = useState(!editMode);
  const [selectedPresetCategory, setSelectedPresetCategory] = useState(null);
  const { setAppSnackbar, langText, setCategoryRefreshTrigger } = useContext(AppContext);
  const layoutText = langText?.layout;
  const commonText = langText?.common;
  const [val, setVal] = useState(editData?.title || undefined);
  const [selecting, setSelecting] = useState(false);
  const [backGroundImages, setBackgroundImages] = useState(undefined);
  const [selectedBackGroundImage, selectBackGroundImage] = useState(editData?.icon || undefined);
  const [customImage, setCustomImage] = useState(null);
  const [customImagePreview, setCustomImagePreview] = useState(null);
  const [categoryID, setCategoryID] = useState(editData?.id || undefined);
  const [categories, setCategories] = useState(undefined);
  const [editing, setEditing] = useState(editMode);
  const [gettingBackgroundImages, setGettingBackgroundImages] = useState(false);
  const [addingLoader, setaddingLoader] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});

  // Fetch background images
  const fetchBackgroundImages = async () => {
    if (gettingBackgroundImages) return;
    setGettingBackgroundImages(true);
    try {
      const response = await dataFetcher(endpoints.getBackgroundImages);
      if (response.success) {
        setBackgroundImages(response.data.filter((item) => parseInt(item.is_default, 10) === 0).reverse());
      }
    } catch (error) {
      // Silent error handling
    } finally {
      setGettingBackgroundImages(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await dataFetcher(endpoints.getMyCategory);
      if (response.success) {
        setCategories(response.data?.map(({ id, title, icon_id }) => ({ name: title, value: id, icon: icon_id })));
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: 'Oops! Something went wrong while fetching your categories',
        });
      }
    } catch (error) {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while fetching your categories' });
    }
  };

  // Add/Edit category
  const addEditCategory = async () => {
    const payload = {
      title: val,
      category_id: categoryID,
      icon: selectedBackGroundImage
    };

    // If custom image is selected, convert to binary and add to payload
    if (customImage) {
      const reader = new FileReader();
      const imageData = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(customImage);
      });

      // Extract base64 data without the data:image/...;base64, prefix
      const base64Data = imageData.split(',')[1];
      payload.custom = base64Data;
    }

    try {
      const response = await dataFetcher(endpoints.addEditCategory, payload);
      if (response.success) {
        setMenuOpen(false);
        // Trigger category refresh globally
        setCategoryRefreshTrigger((prev) => prev + 1);
        setAppSnackbar({
          isVisible: true,
          message: response.message || `Category ${val} was ${categoryID ? 'updated' : 'created'} `,
        });
        // Call the onCategoryCreated callback if provided (for upload modal integration)
        if (onCategoryCreated && !categoryID) {
          onCategoryCreated();
        }
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while creating your category' });
      }
    } catch (error) {
      const responseData = error?.response?.data?.message;
      setAppSnackbar({ isVisible: true, type: 'error', message: responseData || 'Oops! Something went wrong while creating your category' });
    } finally {
      setaddingLoader(false);
    }
  };

  // Delete category
  const deleteCategory = async () => {
    try {
      const response = await dataFetcher(endpoints.deleteMyCategory, { catId: categoryID });
      if (response.success) {
        setAppSnackbar({
          isVisible: true,
          message: 'Deleted Successfully',
        });
        setMenuOpen(false);
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: 'Oops! Something went wrong',
        });
      }
    } catch (error) {
      const responseData = error?.response?.data?.message;
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: responseData || 'Oops! Something went wrong',
      });
    }
  };

  const presetCategories = [
    { title: 'Vacation', icon: Flight, color: '#10B981' },
    { title: 'School', icon: School, color: '#3B82F6' },
    { title: 'Family', icon: People, color: '#EC4899' },
    { title: 'Friends', icon: Group, color: '#8B5CF6' },
    { title: 'Memories', icon: PhotoLibrary, color: '#F59E0B' },
    { title: 'Work', icon: Work, color: '#6366F1' },
    { title: 'Hobbies', icon: Palette, color: '#EF4444' },
    { title: 'Travel', icon: Explore, color: '#14B8A6' },
    { title: 'Events', icon: Event, color: '#F97316' },
    { title: 'Projects', icon: Folder, color: '#84CC16' },
    { title: 'Archive', icon: Archive, color: '#64748B' },
    { title: 'Favorites', icon: Star, color: '#EAB308' }
  ];

  const [categoryImageMap, setCategoryImageMap] = useState({});

  const assignRandomImages = () => {
    const imageMap = {};
    presetCategories.forEach((category) => {
      const randomIndex = Math.floor(Math.random() * backGroundImages.length);
      imageMap[category.title] = backGroundImages[randomIndex]?.id;
    });
    setCategoryImageMap(imageMap);
  };

  // Fetch data only once on mount
  useEffect(() => {
    fetchCategories();
    fetchBackgroundImages();
  }, []);

  // Reset state when modal closes (no API calls here)
  useEffect(() => {
    if (!isMenuOpen) {
      setVal(undefined);
      setSelecting(false);
      setEditing(false);
      setIsPresetMode(true);
      setSelectedPresetCategory(null);
      // Call the external onModalClose callback if provided
      if (onModalClose) {
        onModalClose();
      }
    }
  }, [isMenuOpen, onModalClose]);

  useEffect(() => {
    if (backGroundImages && backGroundImages.length > 0) {
      assignRandomImages();
    }
  }, [backGroundImages]);

  const selectPresetCategory = (preset) => {
    setSelectedPresetCategory(preset.title);
    setVal(preset.title);
    selectBackGroundImage(categoryImageMap[preset.title]);
  };

  const editCategory = (name, value, icon) => {
    setVal(name);
    setCategoryID(value);
    setEditing(true);
    selectBackGroundImage(icon);
  };

  const removeCategory = (name, value) => {
    setVal(name);
    setCategoryID(value);
    if (categoryID) {
      deleteCategory();
    }
  };

  const handleCustomImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check if file is an image
      if (file.type.startsWith('image/')) {
        setCustomImage(file);

        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e) => {
          setCustomImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);

        // Deselect any preset background
        selectBackGroundImage(null);
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: 'Please select a valid image file'
        });
      }
    }
  };

  return (
    <>
      {window.location.pathname.includes('content-layout') && (
        <div className={cn({ [styles.buttonWithText]: true, [styles.placeEnd]: window.screen.width <= 1350 })}>
          <span className={styles.createTitle}>
            {'Categories' || layoutText?.createCategory}
          </span>
          <Tooltip
            title={layoutText?.createCategory || 'Create Categories'}
            aria-label="Create Categories"
            disableHoverListener={!(window.screen.width <= 1350)}
          >
            <CreateCategory
              onClick={() => { setMenuOpen(true); }}
              className={styles.playlistMe}
            />
          </Tooltip>
        </div>
      )}

      <Dialog
        open={isMenuOpen}
        onClose={() => setMenuOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            maxWidth: 650,
            height: '75vh',
            maxHeight: '75vh'
          }
        }}
      >
        <DialogContent style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <div style={{
            padding: 24,
            backgroundColor: '#F8F9FA',
            borderRadius: '16px 16px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
          >
            <h2 style={{
              margin: 0,
              color: '#1A1A1A',
              fontSize: 20,
              fontWeight: 600,
              fontFamily: 'Montserrat, sans-serif'
            }}
            >
              {editing ? (layoutText?.editCategory || 'Edit Category') : (layoutText?.addNewCategory || 'Create New Category')}
            </h2>
            <IconButton
              onClick={() => setMenuOpen(false)}
              style={{ color: '#6B7280' }}
            >
              <Close />
            </IconButton>
          </div>

          {/* Mode Selector */}
          {!editing && (
            <div style={{ padding: '16px 24px' }}>
              <div style={{
                backgroundColor: '#F3F4F6',
                borderRadius: 10,
                padding: 4,
                display: 'flex'
              }}
              >
                <div
                  style={{
                    flex: 1,
                    padding: '10px 0',
                    backgroundColor: isPresetMode ? 'white' : 'transparent',
                    borderRadius: 8,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxShadow: isPresetMode ? '0px 2px 4px rgba(0, 0, 0, 0.05)' : 'none'
                  }}
                  onClick={() => setIsPresetMode(true)}
                >
                  <Category style={{
                    marginRight: 8,
                    fontSize: 18,
                    color: isPresetMode ? '#5aa5ef' : '#6B7280'
                  }}
                  />
                  <span style={{
                    color: isPresetMode ? '#1A1A1A' : '#6B7280',
                    fontSize: 14,
                    fontWeight: isPresetMode ? 600 : 500,
                    fontFamily: 'Montserrat, sans-serif'
                  }}
                  >
                    Choose Preset
                  </span>
                </div>
                <div style={{ width: 4 }} />
                <div
                  style={{
                    flex: 1,
                    padding: '10px 0',
                    backgroundColor: !isPresetMode ? 'white' : 'transparent',
                    borderRadius: 8,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxShadow: !isPresetMode ? '0px 2px 4px rgba(0, 0, 0, 0.05)' : 'none'
                  }}
                  onClick={() => setIsPresetMode(false)}
                >
                  <Edit style={{
                    marginRight: 8,
                    fontSize: 18,
                    color: !isPresetMode ? '#5aa5ef' : '#6B7280'
                  }}
                  />
                  <span style={{
                    color: !isPresetMode ? '#1A1A1A' : '#6B7280',
                    fontSize: 14,
                    fontWeight: !isPresetMode ? 600 : 500,
                    fontFamily: 'Montserrat, sans-serif'
                  }}
                  >
                    Create Custom
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
            {isPresetMode && !editing ? (
              // Preset Categories Grid
              <div>
                <h3 style={{
                  color: '#1A1A1A',
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'Montserrat, sans-serif',
                  margin: '16px 0 8px 0'
                }}
                >
                  Select a Category
                </h3>
                <p style={{
                  color: '#6B7280',
                  fontSize: 12,
                  fontFamily: 'Montserrat, sans-serif',
                  margin: '0 0 16px 0'
                }}
                >
                  Choose from our preset categories with matching backgrounds
                </p>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 12,
                  marginBottom: 16
                }}
                >
                  {presetCategories.map((preset, index) => {
                    const isSelected = selectedPresetCategory === preset.title;
                    const imageId = categoryImageMap[preset.title];
                    const backgroundImage = backGroundImages?.find((img) => img.id === imageId);
                    const IconComponent = preset.icon;
                    const optimizedUrl = getOptimizedImageUrl(backgroundImage?.imagekit_image, 200);
                    const isImageLoaded = loadedImages[preset.title];

                    return (
                      <div
                        key={index}
                        onClick={() => selectPresetCategory(preset)}
                        style={{
                          borderRadius: 12,
                          border: `${isSelected ? '2.5px solid #5aa5ef' : '1px solid #E5E7EB'}`,
                          cursor: 'pointer',
                          position: 'relative',
                          aspectRatio: '1.2',
                          overflow: 'hidden',
                          boxShadow: isSelected ? '0px 2px 8px rgba(90, 165, 239, 0.2)' : 'none',
                          transition: 'all 200ms'
                        }}
                      >
                        {/* Loading skeleton placeholder */}
                        {optimizedUrl && !isImageLoaded && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            background: `linear-gradient(135deg, ${preset.color}40, ${preset.color}60)`,
                            animation: 'pulse 1.5s ease-in-out infinite'
                          }}
                          />
                        )}

                        {/* Background - optimized with lazy loading */}
                        {optimizedUrl ? (
                          <img
                            src={optimizedUrl}
                            alt="background"
                            loading="eager"
                            decoding="async"
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              opacity: isImageLoaded ? 1 : 0,
                              transition: 'opacity 300ms ease-in-out'
                            }}
                            onLoad={() => {
                              setLoadedImages((prev) => ({ ...prev, [preset.title]: true }));
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                        ) : null}

                        {/* Fallback gradient */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          background: `linear-gradient(135deg, ${preset.color}CC, ${preset.color})`,
                          display: optimizedUrl ? 'none' : 'block'
                        }}
                        />

                        {/* Dark overlay */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.6))'
                        }}
                        />

                        {/* Content */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          color: 'white'
                        }}
                        >
                          <IconComponent style={{ fontSize: 28, marginBottom: 8 }} />
                          <span style={{
                            fontSize: 13,
                            fontWeight: 600,
                            fontFamily: 'Montserrat, sans-serif',
                            textAlign: 'center',
                            color: 'white'
                          }}
                          >
                            {preset.title}
                          </span>
                        </div>

                        {/* Selected indicator */}
                        {isSelected && (
                          <div style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 24,
                            height: 24,
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                          >
                            <CheckCircle style={{ color: '#5aa5ef', fontSize: 20 }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              // Custom Category Creation
              <div>
                <h3 style={{
                  color: '#1A1A1A',
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'Montserrat, sans-serif',
                  margin: '16px 0 8px 0'
                }}
                >
                  Category Title
                </h3>

                <TextField
                  value={val || ''}
                  onChange={(e) => setVal(e.target.value)}
                  placeholder="Enter category title"
                  variant="outlined"
                  fullWidth
                  inputProps={{ maxLength: 70 }}
                  style={{
                    marginBottom: 4,
                    backgroundColor: '#F3F4F6',
                    borderRadius: 8
                  }}
                />

                <div style={{ textAlign: 'right', marginBottom: 20 }}>
                  <span style={{
                    color: (val || '').length >= 70 ? '#EF4444' : '#9CA3AF',
                    fontSize: 12,
                    fontFamily: 'Montserrat, sans-serif'
                  }}
                  >
                    {(val || '').length}
                    /70
                  </span>
                </div>

                {/* Background Banner Selection */}
                <h3 style={{
                  color: '#1A1A1A',
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'Montserrat, sans-serif',
                  margin: 0
                }}
                >
                  Background Banner
                </h3>
                <p style={{
                  color: '#6B7280',
                  fontSize: 12,
                  fontFamily: 'Montserrat, sans-serif',
                  margin: '8px 0 16px 0'
                }}
                >
                  Select a background image for your category
                </p>

                {gettingBackgroundImages ? (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 200,
                    marginBottom: 20
                  }}
                  >
                    <CircularProgress style={{ color: '#5aa5ef' }} />
                  </div>
                ) : backGroundImages?.length === 0 ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 200,
                    color: '#6B7280',
                    marginBottom: 20
                  }}
                  >
                    <PhotoLibrary style={{ fontSize: 48, marginBottom: 12 }} />
                    <span style={{
                      fontSize: 14,
                      fontFamily: 'Montserrat, sans-serif'
                    }}
                    >
                      No background images available
                    </span>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 12,
                    marginBottom: 20
                  }}
                  >
                    {/* <div
                      style={{
                        border: customImagePreview ? '2.5px solid #5aa5ef' : '1px dashed #E5E7EB',
                        borderRadius: 8,
                        cursor: 'pointer',
                        overflow: 'hidden',
                        aspectRatio: '16/9',
                        position: 'relative',
                        boxShadow: '0px 2px 6px rgba(90, 165, 239, 0.2)',
                        transition: 'all 200ms',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f9fafb'
                      }}
                      onClick={() => document.getElementById('custom-image-upload').click()}
                    >
                      <input
                        type="file"
                        id="custom-image-upload"
                        accept="image/*"
                        onChange={handleCustomImageUpload}
                        style={{ display: 'none' }}
                      />
                      {customImagePreview ? (
                        <img
                          src={customImagePreview}
                          alt="Custom category background"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#6b7280',
                          textAlign: 'center',
                          padding: '10px'
                        }}
                        >
                          <PhotoLibrary style={{ fontSize: 24, marginBottom: 4 }} />
                          <span style={{ fontSize: '12px', fontWeight: 500 }}>Upload Custom</span>
                          <span style={{ fontSize: '10px' }}>Click to select</span>
                        </div>
                      )}
                    </div> */}
                    {backGroundImages?.map(({ imagekit_image, image, id }) => {
                      const isSelected = selectedBackGroundImage === id;
                      const optimizedUrl = getOptimizedImageUrl(imagekit_image, 150) || image;
                      const isImageLoaded = loadedImages[`bg-${id}`];

                      return (
                        <div
                          key={id}
                          onClick={() => {
                            selectBackGroundImage(id);
                            // Clear custom image when preset is selected
                            setCustomImage(null);
                            setCustomImagePreview(null);
                          }}
                          style={{
                            border: `${isSelected ? '2.5px solid #5aa5ef' : '1px solid #E5E7EB'}`,
                            borderRadius: 8,
                            cursor: 'pointer',
                            overflow: 'hidden',
                            aspectRatio: '16/9',
                            position: 'relative',
                            boxShadow: isSelected ? '0px 2px 6px rgba(90, 165, 239, 0.2)' : 'none',
                            transition: 'all 200ms',
                            backgroundColor: '#f3f4f6'
                          }}
                        >
                          {/* Loading skeleton */}
                          {!isImageLoaded && (
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                              backgroundSize: '200% 100%',
                              animation: 'shimmer 1.5s infinite'
                            }}
                            />
                          )}
                          <img
                            src={optimizedUrl || AlterImage}
                            alt="background"
                            loading="eager"
                            decoding="async"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              opacity: isImageLoaded ? 1 : 0,
                              transition: 'opacity 300ms ease-in-out'
                            }}
                            onLoad={() => {
                              setLoadedImages((prev) => ({ ...prev, [`bg-${id}`]: true }));
                            }}
                            onError={(e) => {
                              e.target.src = AlterImage;
                              setLoadedImages((prev) => ({ ...prev, [`bg-${id}`]: true }));
                            }}
                          />
                          {isSelected && (
                            <div style={{
                              position: 'absolute',
                              top: 6,
                              right: 6,
                              width: 22,
                              height: 22,
                              backgroundColor: '#5aa5ef',
                              borderRadius: '50%',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}
                            >
                              <Check style={{ color: 'white', fontSize: 14 }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Footer actions */}
          <div style={{
            padding: 24,
            backgroundColor: '#F8F9FA',
            borderRadius: '0 0 16px 16px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12
          }}
          >
            <Button
              onClick={() => setMenuOpen(false)}
              style={{
                padding: '12px 24px',
                color: '#6B7280',
                fontSize: 14,
                fontFamily: 'Montserrat, sans-serif'
              }}
            >
              Cancel
            </Button>

            <Button
              disabled={!val?.trim() || addingLoader}
              onClick={() => {
                addEditCategory();
                setaddingLoader(true);
              }}
              variant="contained"
              style={{
                backgroundColor: val?.trim() && !addingLoader ? '#5aa5ef' : '#E5E7EB',
                color: 'white',
                borderRadius: 8,
                padding: '12px 24px',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'Montserrat, sans-serif',
                boxShadow: 'none',
                position: 'relative'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  visibility: addingLoader ? 'hidden' : 'visible'
                }}
              >
                <Add style={{ fontSize: 18 }} />
                {editing ? 'Update Category' : 'Create Category'}
              </div>
              {addingLoader && (
                <CircularProgress
                  size={20}
                  style={{
                    color: 'white',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: -10,
                    marginLeft: -10
                  }}
                />
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateCategoryPage;
